import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AcknowledgeDocument() {
  useEffect(() => { document.title = 'Confirm Receipt — SIYAK'; }, []);

  const { ack_token } = useParams();

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null); // { status, message, document_type, property_address, reference, recipient_name }
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ack_token) {
      setError('No acknowledgement token provided.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/public/document-delivery/acknowledge/${ack_token}`
        );
        setResult(data);
      } catch (err) {
        if (err?.response?.status === 404) {
          setError('This acknowledgement link is not recognised or has expired.');
        } else {
          setError('Something went wrong. Please try again or contact support.');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [ack_token]);

  const isConfirmed = result?.status === 'confirmed';
  const isAlready = result?.status === 'already_confirmed';

  const wrap = (children) => (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 flex flex-col">
      <header className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <a href="https://www.siyak.co.uk" aria-label="SIYAK home" className="inline-flex items-center">
              <img src="/brand/logo-header.png" alt="SIYAK" className="h-8 w-auto sm:h-9" />
            </a>
            <div className="hidden sm:block">
              <p className="text-sm font-semibold text-[#1e3a5f]">Document receipt</p>
              <p className="text-xs text-muted-foreground">You have been sent a tenancy document</p>
            </div>
          </div>
          <Badge variant="outline" className="border-[#1e3a5f]/20 text-[#1e3a5f]">
            Secure confirmation
          </Badge>
        </div>
      </header>

      <main className="flex-1 px-4 py-8 sm:px-6 sm:py-10">
        <div className="mx-auto w-full max-w-2xl">
          {children}
        </div>
      </main>

      <footer className="border-t bg-white/90 px-4 py-6 text-center text-xs text-muted-foreground sm:px-6">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-center gap-2 sm:flex-row sm:gap-3">
          <a href="https://www.siyak.co.uk" aria-label="SIYAK home" className="inline-flex items-center">
            <img src="/brand/logo-header.png" alt="SIYAK" className="h-5 w-auto opacity-70" />
          </a>
          <span className="hidden sm:inline">·</span>
          <span>© {new Date().getFullYear()} SIYAK. All rights reserved.</span>
          <span className="hidden sm:inline">·</span>
          <a href="https://www.siyak.co.uk/privacy" className="hover:text-foreground underline underline-offset-2">Privacy</a>
        </div>
      </footer>
    </div>
  );

  if (loading) {
    return wrap(
      <div className="flex min-h-[280px] items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Confirming your receipt…</span>
      </div>
    );
  }

  if (error) {
    return wrap(
      <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
        <XCircle className="mx-auto mb-4 h-10 w-10 text-red-400" />
        <h1 className="mb-2 text-lg font-semibold text-red-800">Link not recognised</h1>
        <p className="text-sm text-red-700">{error}</p>
        <p className="mt-4 text-xs text-red-600">
          If you believe this is an error, please contact us at{' '}
          <a href="mailto:support@siyak.co.uk" className="underline">support@siyak.co.uk</a>.
        </p>
      </div>
    );
  }

  return wrap(
    <div className="space-y-6">
      <div className="rounded-2xl border border-[#1e3a5f]/10 bg-white/80 p-6 shadow-sm text-center">
        <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-500" />
        <h1 className="mb-1 text-xl font-semibold text-[#1e3a5f]">
          {isAlready ? 'Already acknowledged' : 'Receipt acknowledged'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isAlready
            ? 'You have already acknowledged this document. No further action is needed.'
            : `Thank you${result?.recipient_name ? `, ${result.recipient_name}` : ''}. Your landlord has been notified and a record has been saved.`}
        </p>
      </div>

      {(result?.document_type || result?.property_address || result?.reference) && (
        <div className="rounded-xl border bg-white shadow-sm divide-y text-sm">
          {result.document_type && (
            <div className="flex items-center justify-between px-5 py-3">
              <span className="text-muted-foreground">Document</span>
              <span className="font-medium text-right max-w-[60%]">{result.document_type}</span>
            </div>
          )}
          {result.property_address && (
            <div className="flex items-center justify-between px-5 py-3">
              <span className="text-muted-foreground">Property</span>
              <span className="font-medium text-right max-w-[60%]">{result.property_address}</span>
            </div>
          )}
          {result.reference && (
            <div className="flex items-center justify-between px-5 py-3">
              <span className="text-muted-foreground">Reference</span>
              <span className="font-mono text-xs font-medium">{result.reference}</span>
            </div>
          )}
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground">
        Your landlord has been notified. You can close this page.
      </p>
    </div>
  );
}
